import { ICON_ART_SIZE, artScale } from "odyssey-shared";
import { PLATE_LIFT } from "../health-bars/bar.ts";
import { IDLE_MARK, IDLE_MARK_GAP } from "../nameplates/idle-mark.ts";
import { ABOVE_HEAD, LINE } from "../nameplates/layout.ts";

/**
 * Where an emoji over a character stands and how large it is drawn (ADR-114 decisions 5.2 and 5.3). Its foot stands
 * `EMOJI_GAP` logical pixels over the top of the plate's stack, read from the nameplates' and the bars' own constants
 * (the name row `ABOVE_HEAD` and a line over the head, the health bar's `PLATE_LIFT` while a bar is drawn, the idle and
 * AFK mark where one is drawn), or over the character's bubble while it has one, centred on the character. The art is
 * drawn at k = max(1, floor(16 x d / 32)) device pixels an art pixel, ADR-111 decision 12's rule on a 16 box that is
 * never under 1. As for the plates, every position is computed in device pixels from an anchor that is itself a whole
 * device pixel and then divided by the UI scale, so the emoji's edges stand on the device grid at every UI scale.
 */

/**
 * Logical pixels between the emoji's foot and the top of what it stands over.
 */
export const EMOJI_GAP = 4;

/**
 * The box ADR-111 decision 12's rule sizes an emoji for, in logical pixels.
 */
const EMOJI_BOX = 16;

/**
 * How far over the plate's top an idle and AFK mark reaches, in logical pixels: its gap and the taller of its z's, with
 * their outline, and the word AFK's line.
 */
const IDLE_MARK_REACH = IDLE_MARK_GAP + Math.max(IDLE_MARK.height + IDLE_MARK.outline, LINE.height);

/**
 * An emoji's place, written in place so an emoji placed every frame allocates nothing: its top left in logical
 * pixels, the scale its 32 pixel frame is drawn at in logical pixels, and its box in device pixels.
 */
export interface EmojiLayout {
  x: number;
  y: number;
  displayScale: number;
  deviceLeft: number;
  deviceTop: number;
  deviceSize: number;
}

/**
 * A layout to write into.
 *
 * @returns A layout of zeros.
 */
export function newEmojiLayout(): EmojiLayout {
  return { x: 0, y: 0, displayScale: 0, deviceLeft: 0, deviceTop: 0, deviceSize: 0 };
}

/**
 * How many device pixels square an art pixel is drawn at.
 *
 * @param scale The UI scale d, a whole number above 0.
 * @returns k = max(1, floor(16 x d / 32)): 1 up to d 3, 2 at 4 and 5, 3 at 6.
 * @throws RangeError when the scale is not a whole number above 0.
 * @example
 * emojiScale(4);
 * // 2: the emoji is 64 device pixels square at 2560 by 1440
 */
export function emojiScale(scale: number): number {
  return Math.max(1, artScale(EMOJI_BOX, scale));
}

/**
 * The top of a character's plate stack, which an emoji stands over when the character has no bubble: the name row's
 * top, lifted over a health bar while one is drawn, and over an idle and AFK mark where one is drawn. It is the place
 * the plate would take, so a viewer who turned plates off sees the emoji where it would stand with them on.
 *
 * @param headY The device pixel of the character's head.
 * @param scale The UI scale.
 * @param lifted Whether the character's health bar lifts its plate.
 * @param idleMark Whether an idle and AFK mark is drawn over the plate.
 * @returns The stack's top in device pixels.
 */
export function emojiStackTop(headY: number, scale: number, lifted: boolean, idleMark: boolean): number {
  return headY - ((lifted ? PLATE_LIFT : 0) + ABOVE_HEAD + LINE.height + (idleMark ? IDLE_MARK_REACH : 0)) * scale;
}

/**
 * What an emoji's foot stands over: the character's bubble while it has one, else the plate's stack; whichever is
 * higher, should a bubble ever stand lower than the stack.
 *
 * @param stackTop The plate stack's top in device pixels (`emojiStackTop`).
 * @param bubbleTop The top of the character's bubble frame in device pixels, NaN when no bubble is drawn for it.
 * @returns The device pixel the foot stands `EMOJI_GAP` over.
 */
export function emojiStand(stackTop: number, bubbleTop: number): number {
  return Number.isNaN(bubbleTop) ? stackTop : Math.min(stackTop, bubbleTop);
}

/**
 * Places a still emoji: centred across on the character, its foot `EMOJI_GAP` over the stand, 32 x k device pixels
 * square.
 *
 * @param anchorX The device pixel the emoji is centred on, the character's feet across.
 * @param standY The device pixel its foot stands over (`emojiStand`).
 * @param scale The UI scale.
 * @param k Device pixels an art pixel (`emojiScale`).
 * @param out Where to write the layout.
 * @returns `out`.
 */
export function emojiLayout(anchorX: number, standY: number, scale: number, k: number, out: EmojiLayout): EmojiLayout {
  const size = ICON_ART_SIZE * k;
  const left = anchorX - size / 2;
  const top = standY - EMOJI_GAP * scale - size;
  out.x = left / scale;
  out.y = top / scale;
  out.displayScale = k / scale;
  out.deviceLeft = left;
  out.deviceTop = top;
  out.deviceSize = size;
  return out;
}
